import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { unwrapEntity } from '../../utils/apiPayload';
import SEO from '../../components/SEO';
import Navbar from '../../component/Navbar/Navbar';
import Footer from '../../component/Footer/Footer';
import ApartmentReviews from '../../component/ApartmentReviews/ApartmentReviews';
import { FiMapPin, FiUsers, FiMaximize, FiStar, FiChevronLeft, FiX, FiChevronRight as FiRight, FiChevronLeft as FiLeft, FiImage } from 'react-icons/fi';
import { splitHeroGallery } from '../../utils/galleryHero';
import { playVideoFromUserGesture, playGateInteractionHandlers } from '../../utils/playVideoFromUserGesture';
import './ApartmentDetail.css';

function normalizeAmenities(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item : item?.name || ''))
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];

    if (text.startsWith('[') && text.endsWith(']')) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => String(item).trim()).filter(Boolean);
        }
      } catch {
        // fall through to delimiter split
      }
    }

    return text
      .split(/,|\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

const ApartmentDetail = () => {
  const { apartmentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startQ = searchParams.get('start_date') || '';
  const endQ = searchParams.get('end_date') || '';
  const dateQuery =
    startQ && endQ && endQ > startQ
      ? `?start_date=${encodeURIComponent(startQ)}&end_date=${encodeURIComponent(endQ)}`
      : '';

  const [apartment, setApartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const galleryItemsRef = useRef([]);
  const heroVideoRef = useRef(null);
  const lightboxVideoRef = useRef(null);
  /** iOS: native controls often ignore first taps; a button calling .play() counts as a real user gesture. */
  const [heroPlayGateDismissed, setHeroPlayGateDismissed] = useState(false);
  const [lightboxPlayGateDismissed, setLightboxPlayGateDismissed] = useState(false);
  const heroPlayGateLastAt = useRef(0);
  const lightboxPlayGateLastAt = useRef(0);

  const apartmentPath = `/apartments/${apartmentId}${dateQuery}`;

  const refreshApartment = useCallback(() => {
    return api.get(apartmentPath).then((res) => setApartment(unwrapEntity(res) || res.data || res));
  }, [apartmentPath]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(apartmentPath)
      .then((res) => setApartment(unwrapEntity(res) || res.data || res))
      .catch((err) => setError(err?.message || 'Could not load apartment details'))
      .finally(() => setLoading(false));
  }, [apartmentPath]);

  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });
  const prevImage = () => setLightbox((s) => {
    const n = galleryItemsRef.current.length;
    if (!n) return s;
    return { ...s, index: (s.index - 1 + n) % n };
  });
  const nextImage = () => setLightbox((s) => {
    const n = galleryItemsRef.current.length;
    if (!n) return s;
    return { ...s, index: (s.index + 1) % n };
  });

  useEffect(() => {
    if (!lightbox.open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox.open]);

  useEffect(() => {
    setHeroPlayGateDismissed(false);
  }, [apartmentId]);

  useEffect(() => {
    setLightboxPlayGateDismissed(false);
  }, [lightbox.open, lightbox.index]);

  const fireHeroPlayGate = useCallback((e) => {
    e.stopPropagation();
    const now = Date.now();
    if (now - heroPlayGateLastAt.current < 420) return;
    heroPlayGateLastAt.current = now;
    playVideoFromUserGesture(heroVideoRef.current);
  }, []);

  const fireLightboxPlayGate = useCallback((e) => {
    e.stopPropagation();
    const now = Date.now();
    if (now - lightboxPlayGateLastAt.current < 420) return;
    lightboxPlayGateLastAt.current = now;
    playVideoFromUserGesture(lightboxVideoRef.current);
  }, []);

  const setHeroVideoRef = useCallback((el) => {
    heroVideoRef.current = el;
    if (el) {
      el.setAttribute('playsinline', '');
      el.setAttribute('webkit-playsinline', '');
    }
  }, []);

  const setLightboxVideoRef = useCallback((el) => {
    lightboxVideoRef.current = el;
    if (el) {
      el.setAttribute('playsinline', '');
      el.setAttribute('webkit-playsinline', '');
    }
  }, []);

  if (loading) {
    return (
      <div className="apt-detail-page">
        <Navbar />
        <div className="apt-detail-loading">Loading details…</div>
        <Footer />
      </div>
    );
  }

  if (error || !apartment) {
    return (
      <div className="apt-detail-page">
        <Navbar />
        <div className="apt-detail-error">
          <p>{error || 'Apartment not found.'}</p>
          <Link to="/rooms" className="btn-primary">Browse Apartments</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const property = apartment.property || {};
  const facilityCategories = apartment.facility_categories || {};
  const gallerySource =
    (apartment.apartment_media?.length > 0 && apartment.apartment_media) ||
    (apartment.media?.length > 0 && apartment.media) ||
    property.media ||
    [];
  const legacyItems = gallerySource.map((m) => ({
    id: m.id,
    type: 'image',
    url: m.url || m.original_url || m.preview_url || m,
    thumb: m.thumbnail || m.url || m,
  }));
  const galleryItems = (Array.isArray(apartment.gallery) && apartment.gallery.length > 0)
    ? apartment.gallery.map((item) => ({
      id: item.id,
      type: item.type === 'video' ? 'video' : 'image',
      url: item.url,
      thumb: item.thumbnail || item.url,
    }))
    : legacyItems;
  galleryItemsRef.current = galleryItems;
  const { hero: galleryHero, heroIndex: galleryHeroIndex, stripEntries: galleryStripEntries } = splitHeroGallery(galleryItems);
  const reviewStats = apartment.review_stats || {};
  const avgRating =
    reviewStats.avg_rating != null
      ? Number(reviewStats.avg_rating)
      : apartment.avg_rating || property.avg_rating;
  const aptAmenities = normalizeAmenities(apartment.amenities);
  const propAmenities = normalizeAmenities(property.amenities);

  const priceQuote = apartment.price_quote || null;
  const quoteSubtotal = priceQuote?.subtotal != null ? Number(priceQuote.subtotal) : null;
  const quoteTotal = priceQuote?.total != null ? Number(priceQuote.total) : null;
  const quoteNights = priceQuote?.nights != null ? Number(priceQuote.nights) : null;
  const appliedDiscount = priceQuote?.applied_discount || null;
  const showStayQuote =
    quoteNights != null &&
    quoteNights > 0 &&
    quoteSubtotal != null &&
    quoteTotal != null &&
    appliedDiscount;

  const bookHref =
    startQ && endQ && endQ > startQ
      ? `/book/${apartment.id || apartmentId}?start_date=${encodeURIComponent(startQ)}&end_date=${encodeURIComponent(endQ)}`
      : `/book/${apartment.id || apartmentId}`;

  const aptSeoTitle = `${apartment.name} — ${property.name || 'Luxistt'}`;
  const aptSeoDesc = (apartment.description && String(apartment.description).trim().slice(0, 300))
    || `Book ${apartment.name} in ${property.city?.name || 'Nigeria'}. ${apartment.capacity_adults || 2} adults, ${apartment.size || ''}sqm${apartment.bathrooms ? `, ${apartment.bathrooms} bathroom${apartment.bathrooms > 1 ? 's' : ''}` : ''}. Furnished Luxistt short-stay with ${Object.values(facilityCategories).flat().slice(0, 4).join(', ') || (aptAmenities.slice(0, 4).join(', ') || 'modern amenities')}.`;
  const firstImageItem = galleryItems.find((g) => g.type === 'image');
  const aptImage = firstImageItem ? firstImageItem.url : undefined;
  const aptJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    name: apartment.name,
    description: aptSeoDesc,
    url: `https://luxistt.com/apartments/${apartmentId}`,
    image: aptImage,
    address: {
      '@type': 'PostalAddress',
      streetAddress: property.address_street || '',
      addressLocality: property.city?.name || '',
      addressCountry: 'NG',
    },
    ...(avgRating ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: String(avgRating),
        bestRating: '5',
        ...(reviewStats.count != null ? { ratingCount: String(reviewStats.count) } : {}),
      },
    } : {}),
  };

  return (
    <div className="apt-detail-page">
      <SEO
        title={aptSeoTitle}
        description={aptSeoDesc}
        path={`/apartments/${apartmentId}`}
        image={aptImage}
        jsonLd={aptJsonLd}
      />
      <Navbar />
      <div className="apt-detail-content">
        <button type="button" className="apt-detail-back" onClick={() => navigate(-1)}>
          <FiChevronLeft /> Back
        </button>

        {/* Gallery: large hero (video preferred) + thumbnail strip below */}
        {galleryItems.length > 0 && galleryHero && (
          <div className="apt-gallery-stack">
            <div
              className={`apt-gallery-hero ${galleryHero.type === 'video' ? 'apt-gallery-hero--video' : ''}`}
              onClick={galleryHero.type === 'video' ? undefined : () => openLightbox(galleryHeroIndex)}
              role={galleryHero.type === 'video' ? 'presentation' : 'button'}
              tabIndex={galleryHero.type === 'video' ? -1 : 0}
              onKeyDown={galleryHero.type === 'video' ? undefined : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openLightbox(galleryHeroIndex);
                }
              }}
            >
              {galleryHero.type === 'video' ? (
                <div
                  className={`apt-gallery-hero-video-wrap${!heroPlayGateDismissed ? ' apt-gallery-hero-video-wrap--gated' : ''}`}
                >
                  <video
                    ref={setHeroVideoRef}
                    className="apt-gallery-hero-media"
                    controls={heroPlayGateDismissed}
                    playsInline
                    preload="auto"
                    src={galleryHero.url}
                    poster={
                      (galleryHero.thumb && galleryHero.thumb !== galleryHero.url)
                        ? galleryHero.thumb
                        : (firstImageItem?.url || undefined)
                    }
                    aria-label={`${apartment.name} — property video`}
                    onPlay={() => setHeroPlayGateDismissed(true)}
                    {...{ 'webkit-playsinline': 'true' }}
                  />
                  {!heroPlayGateDismissed ? (
                    <button
                      type="button"
                      className="apt-gallery-hero-play-gate"
                      aria-label="Play property video"
                      {...playGateInteractionHandlers(fireHeroPlayGate)}
                    >
                      <span className="apt-gallery-hero-play-gate-inner" aria-hidden>
                        <span className="apt-gallery-hero-play-ring">
                          <span className="apt-gallery-hero-play-triangle" />
                        </span>
                        <span className="apt-gallery-hero-play-label">Play video</span>
                      </span>
                    </button>
                  ) : null}
                </div>
              ) : (
                <img
                  className="apt-gallery-hero-media"
                  src={galleryHero.url}
                  alt={`${apartment.name} — main view`}
                  loading="eager"
                  decoding="async"
                />
              )}
              <div className="apt-gallery-hero-bar">
                {galleryHero.type === 'video' && (
                  <span className="apt-gallery-hero-badge">Property video</span>
                )}
                <button
                  type="button"
                  className="apt-gallery-view-all apt-gallery-view-all--bar"
                  onClick={(e) => { e.stopPropagation(); openLightbox(0); }}
                >
                  <FiImage aria-hidden />
                  View all {galleryItems.length} {galleryItems.length === 1 ? 'item' : 'items'}
                </button>
              </div>
            </div>
            {galleryStripEntries.length > 0 && (
              <div className="apt-gallery-strip-wrap">
                <p className="apt-gallery-strip-label">Photos & more</p>
                <div className="apt-gallery-strip" role="list">
                  {galleryStripEntries.map(({ item, index }) => (
                    <button
                      key={item.id || `strip-${index}`}
                      type="button"
                      className="apt-gallery-strip-item"
                      onClick={() => openLightbox(index)}
                      role="listitem"
                    >
                      {item.type === 'video' ? (
                        <>
                          <video
                            className="apt-gallery-strip-media"
                            muted
                            playsInline
                            preload="metadata"
                            src={item.url}
                            {...{ 'webkit-playsinline': 'true' }}
                          />
                          <span className="apt-gallery-video-badge apt-gallery-video-badge--strip" aria-hidden>
                            <span className="apt-gallery-play-ring">
                              <span className="apt-gallery-play-triangle" />
                            </span>
                          </span>
                        </>
                      ) : (
                        <img
                          className="apt-gallery-strip-media"
                          src={item.thumb || item.url}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lightbox */}
        {lightbox.open && galleryItems.length > 0 && (
          <div className="apt-lightbox" onClick={closeLightbox}>
            <button type="button" className="apt-lightbox-close" onClick={closeLightbox}><FiX /></button>
            <button type="button" className="apt-lightbox-nav apt-lightbox-prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}><FiLeft /></button>
            <div className="apt-lightbox-content" onClick={(e) => e.stopPropagation()}>
              {galleryItems[lightbox.index].type === 'video' ? (
                <div
                  className={`apt-lightbox-video-wrap${!lightboxPlayGateDismissed ? ' apt-lightbox-video-wrap--gated' : ''}`}
                >
                  <video
                    ref={setLightboxVideoRef}
                    className="apt-lightbox-video"
                    controls={lightboxPlayGateDismissed}
                    playsInline
                    preload="auto"
                    src={galleryItems[lightbox.index].url}
                    poster={
                      (() => {
                        const v = galleryItems[lightbox.index];
                        if (v.thumb && v.thumb !== v.url) return v.thumb;
                        const img = galleryItems.find((g) => g.type === 'image');
                        return img?.url || undefined;
                      })()
                    }
                    aria-label={`${apartment.name} — video`}
                    onPlay={() => setLightboxPlayGateDismissed(true)}
                    {...{ 'webkit-playsinline': 'true' }}
                  />
                  {!lightboxPlayGateDismissed ? (
                    <button
                      type="button"
                      className="apt-lightbox-play-gate"
                      aria-label="Play video"
                      {...playGateInteractionHandlers(fireLightboxPlayGate)}
                    >
                      <span className="apt-gallery-play-ring apt-lightbox-play-gate-ring" aria-hidden>
                        <span className="apt-gallery-play-triangle" />
                      </span>
                      <span className="apt-lightbox-play-gate-text">Play</span>
                    </button>
                  ) : null}
                </div>
              ) : (
                <img src={galleryItems[lightbox.index].url} alt={`${apartment.name} ${lightbox.index + 1}`} />
              )}
              <div className="apt-lightbox-counter">{lightbox.index + 1} / {galleryItems.length}</div>
            </div>
            <button type="button" className="apt-lightbox-nav apt-lightbox-next" onClick={(e) => { e.stopPropagation(); nextImage(); }}><FiRight /></button>
          </div>
        )}

        <div className="apt-detail-grid">
          <div className="apt-detail-info">
            <h1 className="apt-detail-title">{apartment.name}</h1>
            <p className="apt-detail-property">
              <FiMapPin /> {property.name}{property.address ? ` — ${property.address}` : ''}
            </p>

            <div className="apt-detail-meta">
              {apartment.type && <span className="apt-detail-badge">{apartment.type}</span>}
              {apartment.room_count != null && apartment.room_count > 0 && (
                <span className="apt-detail-badge">{apartment.room_count} room{apartment.room_count !== 1 ? 's' : ''}</span>
              )}
              {apartment.size && <span className="apt-detail-badge"><FiMaximize /> {apartment.size} m²</span>}
              {apartment.bathrooms != null && <span className="apt-detail-badge">{apartment.bathrooms} Bathroom(s)</span>}
              <span className="apt-detail-badge"><FiUsers /> {apartment.capacity_adults || '—'} Adults, {apartment.capacity_children ?? 0} Children</span>
              {apartment.bed_lists && <span className="apt-detail-badge">{apartment.bed_lists}</span>}
              {avgRating && <span className="apt-detail-badge apt-detail-badge-rating"><FiStar /> {Number(avgRating).toFixed(1)}</span>}
            </div>

            {apartment.description && String(apartment.description).trim() && (
              <div className="apt-detail-description glass-panel">
                <h2>About this space</h2>
                <div className="apt-detail-description-body">{apartment.description}</div>
              </div>
            )}

            {aptAmenities.length > 0 && (
              <div className="apt-detail-amenities-block">
                <h2>What&apos;s included</h2>
                <ul className="apt-amenity-tags" aria-label="Apartment amenities">
                  {aptAmenities.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              </div>
            )}

            {propAmenities.length > 0 && (
              <div className="apt-detail-property-about glass-panel">
                <h2>Property highlights</h2>
                <ul className="apt-amenity-tags apt-amenity-tags-muted" aria-label="Property amenities">
                  {propAmenities.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Facility categories */}
            {Object.keys(facilityCategories).length > 0 && (
              <div className="apt-detail-facilities">
                <h2>Amenities & Facilities</h2>
                <div className="apt-detail-facility-groups">
                  {Object.entries(facilityCategories).map(([category, items]) => (
                    <div key={category} className="apt-detail-facility-group">
                      <h3>{category}</h3>
                      <ul>
                        {(Array.isArray(items) ? items : []).map((item, i) => (
                          <li key={i}>{typeof item === 'string' ? item : item.name || item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ApartmentReviews
              apartmentId={apartment.id || apartmentId}
              apartment={apartment}
              onRefreshApartment={refreshApartment}
            />
          </div>

          <div className="apt-detail-sidebar">
            <div className="apt-detail-price-card glass-panel">
              {apartment.price != null && (
                <div className="apt-detail-price">
                  <span className="apt-detail-price-amount">₦{Number(apartment.price).toLocaleString()}</span>
                  <span className="apt-detail-price-unit">/ night</span>
                </div>
              )}
              {showStayQuote && (
                <div className="apt-detail-stay-quote" aria-live="polite">
                  <p className="apt-detail-stay-quote-label">
                    Your dates ({quoteNights} night{quoteNights !== 1 ? 's' : ''})
                  </p>
                  <div className="apt-detail-stay-quote-prices">
                    <span className="apt-detail-price-strike">₦{quoteSubtotal.toLocaleString()}</span>
                    <span className="apt-detail-price-final">₦{quoteTotal.toLocaleString()}</span>
                  </div>
                  {appliedDiscount?.name && (
                    <p className="apt-detail-discount-tag">
                      {appliedDiscount.name} · {Number(appliedDiscount.percent)}% off
                    </p>
                  )}
                </div>
              )}
              {startQ && endQ && endQ > startQ && !showStayQuote && quoteTotal != null && quoteTotal >= 0 && (
                <div className="apt-detail-stay-quote apt-detail-stay-quote--plain">
                  <p className="apt-detail-stay-quote-label">
                    {quoteNights != null && quoteNights > 0 ? `${quoteNights} night${quoteNights !== 1 ? 's' : ''} total` : 'Stay total'}
                  </p>
                  <div className="apt-detail-stay-quote-prices apt-detail-stay-quote-prices-single">
                    <span className="apt-detail-price-final">₦{quoteTotal.toLocaleString()}</span>
                  </div>
                </div>
              )}
              <Link to={bookHref} className="btn-primary apt-detail-book-btn">
                Book this stay
              </Link>
              <p className="apt-detail-price-note">Book through Luxistt — pay securely with Paystack or choose cash on arrival.</p>
              {!(startQ && endQ) && (
                <p className="apt-detail-price-hint">Pick check-in and check-out on Rooms search to see stay total and any discounts here.</p>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ApartmentDetail;
